import {
  BasicProps,
  SearchTextItem,
  SearchFormParams,
  SearchDateItem,
  SearchSelectItem,
  SearchSwitchItem,
  SearchItemBasic,
  SearchButtonsItem,
  SearchRadioItem,
  SearchFormDefaultProps,
  SearchTableItemParams,
} from "@/types";
import { useElementSize } from "@/hooks/shared/element";
import { useDebounceFc } from "@/hooks/shared/debounce";
import {
  Col,
  DatePicker,
  DatePickerProps,
  Empty,
  Input,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Tooltip,
} from "antd";
import type { CheckboxOptionType, SelectProps } from "antd";
import classNames from "classnames";
import React, {
  ChangeEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DefaultOptionType } from "antd/es/select";
import dayjs from "dayjs";
import { RightOutlined } from "@ant-design/icons";

const componentMap = {
  text: TextItem,
  date: DatePickItem,
  select: SelectItem,
  radio: RadioItem,
  switch: SwitchItem,
  buttons: ButtonsItem,
} as const;

type SeachFormComponentMap = typeof componentMap;
type SearchFormNodeItemType = keyof SeachFormComponentMap;

type SearchFormComponentPropsFor<T extends SearchFormNodeItemType> =
  T extends keyof SeachFormComponentMap
    ? React.ComponentProps<SeachFormComponentMap[T]>
    : never;

function generateSearchItemNode<T extends SearchFormNodeItemType>(
  type: T,
  props: SearchFormComponentPropsFor<T>,
): React.ReactElement {
  const Component = componentMap[type] as React.FC<any>;
  return <Component {...props} key={props.config.key} />;
}

const DefaultSearchFormProps: Required<SearchFormDefaultProps> = {
  hasCollspaed: true,
  collapsed: true,
};

export default function SearchForm<T extends Record<string, unknown>>(
  props: SearchFormParams<T>,
) {
  const { className, items, onFormChange, values, defaultConfig } = props;
  const hasCollapsed =
    defaultConfig?.hasCollspaed ?? DefaultSearchFormProps.hasCollspaed;
  const [collapsed, setCollapsed] = useState(false);

  const [wrapperRef, wrapperSize] = useElementSize();

  const UnitPerRow = useMemo(() => {
    if (wrapperSize.width <= 0) return -1;
    if (wrapperSize.width < 1000) {
      return 3;
    } else if (wrapperSize.width < 1600) {
      return 4;
    } else {
      return 6;
    }
  }, [wrapperSize.width]);

  const rows = useMemo(() => {
    const data: [ReactNode, number][][] = [];
    if (UnitPerRow === -1) return data;
    let index = 0,
      curSpan = 0;
    let curRow: [ReactNode, number][] = [];

    // filter items without parentKey
    const itemsWithoutParentKey = items.filter((item) => !item.parentKey);
    const parentChildMap = Object.fromEntries(
      items
        .filter((item) => item.parentKey)
        .map((item) => [item.parentKey, item]),
    );

    while (index < itemsWithoutParentKey.length) {
      const config = itemsWithoutParentKey[index];
      if (config.span + curSpan > UnitPerRow) {
        data.push(curRow);
        curSpan = 0;
        curRow = [];
      }
      curSpan += config.span;
      const nodeProps = {
        showHeader: !collapsed,
        onItemValueChange: (value: unknown) => onFormChange(config.key, value),
        config: config,
        value: values[config.key],
      } as SearchFormComponentPropsFor<SearchFormNodeItemType>;

      if (config.key in parentChildMap) {
        const dependent = generateSearchItemNode(
          parentChildMap[config.key].type,
          {
            loading: false,
            showHeader: false,
            config: parentChildMap[config.key],
            value: values[parentChildMap[config.key].key],
            onItemValueChange: (value: unknown) =>
              onFormChange(parentChildMap[config.key].key, value),
          } as SearchFormComponentPropsFor<SearchFormNodeItemType>,
        );
        nodeProps.dependent = dependent;
      }

      if (config.type === "text") {
        curRow.push([
          generateSearchItemNode(
            config.type,
            nodeProps as SearchFormComponentPropsFor<"text">,
          ),
          config.span,
        ]);
      } else if (config.type === "date") {
        curRow.push([
          generateSearchItemNode(
            config.type,
            nodeProps as SearchFormComponentPropsFor<"date">,
          ),
          config.span,
        ]);
      } else if (config.type === "select") {
        curRow.push([
          generateSearchItemNode(
            config.type,
            nodeProps as SearchFormComponentPropsFor<"select">,
          ),
          config.span,
        ]);
      } else if (config.type === "radio") {
        curRow.push([
          generateSearchItemNode(
            config.type,
            nodeProps as SearchFormComponentPropsFor<"radio">,
          ),
          config.span,
        ]);
      } else if (config.type === "switch") {
        curRow.push([
          generateSearchItemNode(
            config.type,
            nodeProps as SearchFormComponentPropsFor<"switch">,
          ),
          config.span,
        ]);
      } else if (config.type === "buttons") {
        curRow.push([
          generateSearchItemNode(
            config.type,
            nodeProps as SearchFormComponentPropsFor<"buttons">,
          ),
          config.span,
        ]);
      }
      index++;
    }
    if (curRow.length > 0) data.push(curRow);
    return data;
  }, [UnitPerRow, items, values, onFormChange, collapsed]);

  return (
    <div
      className={classNames(className, "flex flex-row shrink-0")}
      style={{
        height: collapsed ? 45 : 12 + rows.length * 71 + (rows.length - 1) * 4,
        transition: "height 0.3s ease",
        overflow: "hidden",
      }}
    >
      {hasCollapsed && (
        <div
          className={classNames(
            "w-8 flex items-center justify-center hover:cursor-pointer",
          )}
          style={{
            borderTopLeftRadius: "inherit",
            backgroundColor: "rgb(22, 119, 255)",
          }}
          onClick={() => setCollapsed(!collapsed)}
        >
          <RightOutlined
            className={classNames(
              "!text-white text-[16px] transition",
              collapsed ? "rotate-0" : "rotate-90",
            )}
          />
        </div>
      )}
      <div
        ref={wrapperRef}
        className={classNames(
          "w-full flex px-2 gap-[4px] pb-[4px]",
          collapsed
            ? "items-center pt-[2px]"
            : "flex-col items-center pt-[8px]",
        )}
      >
        {collapsed ? (
          <Row
            gutter={[20, 0]}
            className="w-full h-[36px] flex items-center !m-0"
          >
            {(rows[0] ?? []).map(([node, span], nodeIndex) => (
              <Col span={(24 / UnitPerRow) * span} key={nodeIndex}>
                {node}
              </Col>
            ))}
          </Row>
        ) : (
          rows.map((row, rowIndex) => (
            <Row gutter={[20, 0]} key={rowIndex} className="w-full">
              {row.map(([node, span], nodeIndex) => (
                <Col span={(24 / UnitPerRow) * span} key={nodeIndex}>
                  {node}
                </Col>
              ))}
            </Row>
          ))
        )}
      </div>
    </div>
  );
}

function BasicItem<T>(
  props: BasicProps & {
    showHeader?: boolean;
    config: Omit<SearchItemBasic<T>, "pageQueryConfig">;
  },
) {
  const { children, config, showHeader = true } = props;
  return (
    <div className="flex flex-col">
      <div
        className={classNames(
          "flex items-center select-none overflow-hidden whitespace-nowrap",
          showHeader ? "gap-2 mb-1 h-[17px]" : "gap-0 h-0",
        )}
      >
        <span className="font-news text-slate-700 !text-[13px]">
          {config.title}
        </span>
        {config.desc && (
          <Tooltip title={config.desc}>
            <span className="text-slate-600 italic text-[13px] overflow-hidden text-ellipsis whitespace-nowrap">
              ({config.desc})
            </span>
          </Tooltip>
        )}
      </div>
      {children}
    </div>
  );
}

function TextItem({
  config,
  onItemValueChange,
  value,
  showHeader,
  dependent,
}: SearchTableItemParams<string, SearchTextItem>) {
  const [errorMsg, setErrorMsg] = useState<string>("");
  const onValueChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value;
      if (config.upper) {
        value = value.toUpperCase();
      }
      if (!value) {
        setErrorMsg("");
        onItemValueChange(value);
        return;
      }
      const [flag, msg] = config.validate?.(value) ?? [true, ""];
      if (flag) {
        setErrorMsg("");
        onItemValueChange(value);
      } else {
        setErrorMsg(msg ?? "");
      }
    },
    [config, onItemValueChange],
  );

  return (
    <BasicItem<string> config={config} showHeader={showHeader}>
      <Space.Compact block>
        <Input
          inputMode={config?.inputMode ?? "text"}
          allowClear
          onChange={onValueChange}
          placeholder={config.placeHolder}
          value={value}
        />
        {dependent}
      </Space.Compact>
      {showHeader && (
        <span className="text-rose-400 mt-[2px] h-[16px]">{errorMsg}</span>
      )}
    </BasicItem>
  );
}

function DatePickItem({
  config,
  onItemValueChange,
  value,
  showHeader,
  dependent,
}: SearchTableItemParams<Date | undefined, SearchDateItem>) {
  const onDateChange: DatePickerProps["onChange"] = (value) => {
    onItemValueChange(value?.toDate() ?? undefined);
  };

  return (
    <BasicItem config={config} showHeader={showHeader}>
      <Space.Compact block>
        <DatePicker
          className={classNames(dependent ? "" : "w-full")}
          onChange={onDateChange}
          allowClear
          mode={config.mode}
          value={value ? dayjs(value) : null}
          showTime={config.showTime}
        />
        {dependent}
      </Space.Compact>
      {showHeader && <span className="mt-[2px] h-[16px]"></span>}
    </BasicItem>
  );
}

function SwitchItem({
  config,
  loading,
  onItemValueChange,
  value,
  showHeader,
}: SearchTableItemParams<unknown, SearchSwitchItem>) {
  const checked = useMemo(() => {
    if (typeof value === "boolean") return Boolean(value);
    else if (typeof value === "string") {
      return Boolean(value === config.checkedValue);
    } else {
      return undefined;
    }
  }, [value, config]);

  return (
    <BasicItem config={config} showHeader={showHeader}>
      <div className="flex items-center gap-2 h-[32px]">
        <span
          className={classNames(
            "font-news text-slate-400 text-[15px]",
            !checked && "text-slate-700",
          )}
        >
          {config.unCheckedTitle}
        </span>
        <Switch
          className="w-12"
          onChange={(value) =>
            onItemValueChange(
              value ? config.checkedValue : config.unCheckedValue,
            )
          }
          loading={loading}
          value={checked}
        />
        <span
          className={classNames(
            "font-news text-slate-400 text-[15px]",
            checked && "text-slate-700",
          )}
        >
          {config.checkedTitle}
        </span>
      </div>
      {showHeader && <span className="mt-[2px] h-[16px]"></span>}
    </BasicItem>
  );
}

function RadioItem({
  config,
  onItemValueChange,
  value,
  showHeader,
  dependent,
}: SearchTableItemParams<
  string | number | boolean | null | undefined,
  SearchRadioItem
>) {
  return (
    <BasicItem config={config} showHeader={showHeader}>
      <Space.Compact block>
        <Radio.Group
          className={config.className}
          block
          value={value}
          options={config.options as CheckboxOptionType[]}
          defaultValue={config.defaultValue}
          optionType="button"
          buttonStyle="solid"
          onChange={(e) => onItemValueChange(e.target.value)}
        />
        {dependent}
      </Space.Compact>
      {showHeader && <span className="mt-[2px] h-[16px]"></span>}
    </BasicItem>
  );
}

function SelectItem({
  config,
  onItemValueChange,
  value,
  showHeader,
  dependent,
}: SearchTableItemParams<unknown, SearchSelectItem>) {
  const [options, setOptions] = useState<SelectProps["options"]>([]);
  const firstFocus = useRef<boolean>(config.lazy ?? true);
  const [loading, setLoading] = useState<boolean>(true);

  const onValueChange: SelectProps["onChange"] = useCallback(
    (value: unknown) => {
      onItemValueChange(value);
    },
    [onItemValueChange],
  );

  const onSearchHandle_ = useCallback(
    (search: string) => {
      if (config.options instanceof Function) {
        setLoading(true);
        config
          .options(search)
          .then((res) => {
            setOptions(
              res.map(
                (one) =>
                  config.dataToOption?.(one) ?? (one as DefaultOptionType),
              ),
            );
          })
          .finally(() => {
            setLoading(false);
          });
      }
    },
    [config],
  );
  const onSearchHandle = useDebounceFc(onSearchHandle_, 500);

  useEffect(() => {
    if (!(config.options instanceof Function)) {
      setOptions(
        config.options.map(
          (one) => config.dataToOption?.(one) ?? (one as DefaultOptionType),
        ),
      );
    } else if (config.preloadOptions) {
      onSearchHandle_("");
    }
  }, [config, onSearchHandle_]);

  return (
    <BasicItem config={config} showHeader={showHeader}>
      <Space.Compact block>
        <Select
          value={value}
          showSearch={config.showSearch ?? true}
          mode={config.mode}
          placeholder={config.placeHolder}
          onChange={onValueChange}
          style={{ width: "100%" }}
          onSearch={onSearchHandle}
          options={options}
          allowClear
          filterOption={!(config.options instanceof Function)}
          maxTagCount={"responsive" as const}
          onFocus={() => {
            if (firstFocus.current) {
              firstFocus.current = false;
              onSearchHandle("");
            }
          }}
          notFoundContent={
            loading ? (
              <Spin size="small" />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )
          }
          maxTagPlaceholder={(omittedValues) => (
            <Tooltip title={omittedValues.map(({ label }) => label).join(", ")}>
              <span>{`+ ${omittedValues.length} ...`}</span>
            </Tooltip>
          )}
        />
        {dependent}
      </Space.Compact>
      {showHeader && <span className="mt-[2px] h-[16px]"></span>}
    </BasicItem>
  );
}

function ButtonsItem({
  config,
  loading,
  showHeader,
}: SearchTableItemParams<unknown, SearchButtonsItem>) {
  return (
    <BasicItem config={config} showHeader={showHeader}>
      {config.render(loading)}
      {showHeader && <span className="mt-[2px] h-[16px]"></span>}
    </BasicItem>
  );
}
